import React from 'react';
import { Box, Typography } from '@mui/material';
import { Server, Cpu, MemoryStick, HardDrive, Clock } from 'lucide-react';
import GlassPaper from '../../GlassPaper';
import MetricGauge from './MetricGauge';
import StatPill from './StatPill';

function fmtBytes(bytes) {
    if (!bytes || !Number.isFinite(bytes)) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0, v = bytes;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}
function fmtUptime(sec) {
    if (!sec) return '—';
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    if (d > 0) return `${d}d ${h}h`;
    const m = Math.floor((sec % 3600) / 60);
    return `${h}h ${m}m`;
}

export default function ProxmoxNodeCard({ node }) {
    const statusTone = node.status === 'online' ? 'good' : node.status === 'offline' ? 'crit' : 'warn';
    return (
        <GlassPaper sx={{
            p: { xs: 2, sm: 2.5 },
            height: '100%',
            background: 'linear-gradient(135deg, rgba(168,85,247,0.12), rgba(59,130,246,0.08))',
            position: 'relative',
            overflow: 'hidden',
        }}>
            <Box sx={{
                position: 'absolute', right: -50, top: -50, width: 200, height: 200,
                background: 'radial-gradient(circle, rgba(168,85,247,0.2), transparent 70%)',
                pointerEvents: 'none',
            }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(168,85,247,0.25), rgba(59,130,246,0.2))',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#c4b5fd',
                    }}><Server size={20} /></Box>
                    <Box>
                        <Typography sx={{ fontSize: 11, letterSpacing: 1.2, opacity: 0.7, textTransform: 'uppercase', fontWeight: 600 }}>Proxmox Node</Typography>
                        <Typography sx={{ fontSize: { xs: 17, sm: 19 }, fontWeight: 700, lineHeight: 1.1 }}>{node.node}</Typography>
                    </Box>
                </Box>
                <StatPill tone={statusTone}>{node.status}</StatPill>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)' },
                gap: 1,
                mb: 2,
            }}>
                <MetricGauge value={node.cpuPct} label="CPU" size={86} stroke={8} />
                <MetricGauge value={node.memPct} label="Memory" size={86} stroke={8} />
                <MetricGauge value={node.rootDiskPct} label="Root" size={86} stroke={8} />
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' },
                gap: 1,
                fontSize: 12,
            }}>
                <Detail icon={<Cpu size={13} />} label="Cores" value={node.cpuCores || '—'} />
                <Detail icon={<MemoryStick size={13} />} label="Mem" value={`${fmtBytes(node.memUsedBytes)} / ${fmtBytes(node.memTotalBytes)}`} />
                <Detail icon={<HardDrive size={13} />} label="Disk" value={`${fmtBytes(node.rootDiskBytes)} / ${fmtBytes(node.rootDiskTotalBytes)}`} />
                <Detail icon={<Clock size={13} />} label="Uptime" value={fmtUptime(node.uptimeSec)} />
            </Box>
        </GlassPaper>
    );
}

function Detail({ icon, label, value }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.65, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
                {icon}{label}
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        </Box>
    );
}
